import type {
  BoardFilters,
  ReportPayload,
  SubmittedReport,
  WidgetBoardKpis,
  WidgetBoardRow,
  WidgetChangelogRow,
  WidgetCommentRow,
  WidgetReportDetail,
  WidgetReportRow,
} from '../types'

/** Page envelope DRF returns for paginated list endpoints. */
export interface BoardListPage {
  count: number
  next: string | null
  previous: string | null
  results: WidgetBoardRow[]
}

export interface ApiClientOptions {
  apiKey: string
  endpoint: string
  fetch?: typeof fetch
  beforeSend?: (payload: ReportPayload) => ReportPayload | false | Promise<ReportPayload | false>
  /**
   * v0.13 — signed-identity callback. Returns the current
   * `(userHash, exp, email)` triple if the host has called
   * `identify({userHash, exp, ...})`. The API client reads this fresh
   * on every request so a delayed `identify()` lights up signed
   * headers on subsequent calls. Return `null` for the legacy
   * unsigned path (the default until the project issues a
   * `WidgetSigningSecret`).
   */
  getSignedIdentity?: () => { userHash: string; exp: number; email: string } | null
}

export interface ApiClient {
  submitReport(payload: ReportPayload): Promise<SubmittedReport>
  /** POST /v1/widget/visibility/ — whether this end-user may see the widget
   *  (Phase 4 allowlist). `email`, when the host identified one, lets an
   *  email-based allowlist match (the backend matches external_id OR email).
   *  Throws on error so the caller can fail closed. */
  checkVisibility(externalId: string, email?: string): Promise<boolean>
  /** GET /v1/reports/widget/mine/ — caller's own reports on this project. */
  listMine(externalId: string): Promise<WidgetReportRow[]>
  /** GET /v1/reports/widget/changelog/ — caller's resolved reports for the
   *  "This week" tab; client groups by ISO week of `resolved_at`. */
  listChangelog(externalId: string): Promise<WidgetChangelogRow[]>
  /** GET /v1/reports/widget/<id>/ — single report + thread. */
  getReport(reportId: string, externalId: string): Promise<WidgetReportDetail>
  /** POST /v1/reports/widget/<id>/comments/ — submitter follow-up. */
  addComment(
    reportId: string,
    externalId: string,
    body: string,
    clientNonce?: string,
  ): Promise<WidgetCommentRow>
  /** PATCH /v1/reports/widget/<id>/ — close-as-resolved by the submitter. */
  closeAsResolved(
    reportId: string,
    externalId: string,
  ): Promise<WidgetReportDetail>
  /** PATCH /v1/reports/widget/<id>/ — reopen (→ in_progress) when the
   *  submitter says the shipped fix didn't actually resolve the issue. */
  reopenUnresolved(
    reportId: string,
    externalId: string,
  ): Promise<WidgetReportDetail>
  /** GET /v1/reports/widget/board/ — paginated, filtered list backing the
   *  v0.12 Board tab. Scope follows the project's
   *  `share_reports_with_widget` flag (submitter-only when off, project-
   *  wide when on); `filters.mine = true` narrows even when on. */
  listBoard(externalId: string, filters?: BoardFilters): Promise<BoardListPage>
  /** GET /v1/reports/widget/board/kpis/ — total + per-status counts +
   *  resolution rate. Cheap; safe to poll alongside the list. */
  fetchBoardKpis(externalId: string, filters?: BoardFilters): Promise<WidgetBoardKpis>
}

const SCALAR_FIELDS: Array<keyof ReportPayload> = [
  'description', 'feedback_type', 'severity', 'env', 'page_url', 'user_agent', 'capture_method',
]

export function assertSafeEndpoint(endpoint: string): void {
  let parsed: URL
  try {
    parsed = new URL(endpoint)
  } catch {
    throw new Error(
      `[mhosaic-feedback] \`endpoint\` is not a valid URL: ${endpoint}`,
    )
  }
  if (parsed.protocol === 'https:') return
  if (
    parsed.protocol === 'http:' &&
    (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1' || parsed.hostname === '[::1]')
  ) {
    return
  }
  throw new Error(
    `[mhosaic-feedback] \`endpoint\` must use https:// (got ${parsed.protocol}//${parsed.hostname}). ` +
      'http:// is only allowed for localhost in dev.',
  )
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  // No silent fallback to a hardcoded host — a misconfigured deploy used
  // to leak reports to https://core.mhosaic.com (which doesn't even
  // resolve). Failing fast at construction makes the misconfig obvious.
  const endpoint = (options.endpoint ?? '').replace(/\/+$/, '')
  if (!endpoint) {
    throw new Error(
      '[mhosaic-feedback] `endpoint` is required (e.g. "https://feedback.example.com").',
    )
  }
  // SECURITY: endpoint is host-controlled config. Without a scheme guard a
  // misconfigured host (or a host XSS that injects a `&lt;script
  // data-endpoint="http://attacker"&gt;` embed tag) can steer the widget at
  // any URL and exfiltrate every captured report — including the
  // pk_proj_ key in the Authorization header — to attacker-controlled
  // infra. Only allow https:; permit http:// for localhost/127.0.0.1 so
  // dev sandboxes keep working.
  assertSafeEndpoint(endpoint)
  const fetcher = options.fetch ?? globalThis.fetch

  async function submitReport(input: ReportPayload): Promise<SubmittedReport> {
    let payload: ReportPayload | false = input
    if (options.beforeSend) payload = await options.beforeSend(input)
    if (payload === false) throw new Error('Submission cancelled by beforeSend')

    const form = new FormData()
    for (const field of SCALAR_FIELDS) {
      form.append(field, String(payload[field]))
    }
    form.append('technical_context', JSON.stringify(payload.technical_context))
    if (payload.screenshot) form.append('screenshot', payload.screenshot, 'screenshot.png')
    // Only emit `synthetic` when truthy — a `false` value would still trigger
    // the BooleanField parser on the backend, which is fine, but omitting it
    // keeps the request shape byte-identical for existing curated submissions.
    if (payload.synthetic) form.append('synthetic', 'true')
    // v0.7: nest the identity payload so DRF's WidgetUserIdentitySerializer
    // sees a real object. FormData can't carry nested objects directly —
    // use a JSON-encoded string under the `user` key; DRF parses it via
    // the JSONField semantics on the WidgetUser fields.
    if (payload.user?.id) {
      form.append('user', JSON.stringify(payload.user))
    }
    // v0.7.3: widget_version is build-time-stamped by tsup. Lets the
    // backend show "currently running v0.7.2" per project on the
    // operator Companies page (per-customer version observability).
    if (payload.widget_version) {
      form.append('widget_version', payload.widget_version)
    }

    // The backend honors `synthetic=true` only when this header is set,
    // so a hand-crafted curl with the public key can't smuggle a curated
    // report into the auto-error bucket (where the default operator view
    // hides it). The bundled debugger always sets payload.synthetic; the
    // user-facing widget never does.
    const headers: Record<string, string> = {
      Authorization: `Bearer ${options.apiKey}`,
    }
    if (payload.synthetic) {
      headers['X-Mhosaic-Capture-Source'] = 'error-tracking'
    }
    // Signed-identity headers on the SUBMIT path too: when the host has
    // wired up identify({userHash, exp, email}) and the backend project
    // has a WidgetSigningSecret, the request must carry the HMAC. We
    // also re-stamp `X-Mhosaic-User` so the verifier sees the
    // signed-over external_id directly (the body `user` block is still
    // sent — the backend prefers the header pair when signing is in
    // effect; see views.py:CreateFeedbackReportView.post).
    const signed = options.getSignedIdentity?.()
    if (signed && signed.userHash && signed.exp && payload.user?.id) {
      headers['X-Mhosaic-User'] = String(payload.user.id)
      headers['X-Mhosaic-User-Hmac'] = signed.userHash
      headers['X-Mhosaic-User-Exp'] = String(signed.exp)
      if (signed.email) headers['X-Mhosaic-User-Email'] = signed.email
    }
    const response = await fetcher(`${endpoint}/api/feedback/v1/reports/`, {
      method: 'POST',
      headers,
      body: form,
    })
    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(`Feedback submit failed: ${response.status} ${text}`)
    }
    return response.json() as Promise<SubmittedReport>
  }

  function widgetHeaders(externalId: string): Record<string, string> {
    const headers: Record<string, string> = {
      Authorization: `Bearer ${options.apiKey}`,
      'X-Mhosaic-User': externalId,
    }
    // Signed-identity Phase 1 (v0.13): forward the HMAC envelope when the
    // host has supplied one via `identify({userHash, exp, email})`. The
    // backend gates on the project's `WidgetSigningSecret` row — projects
    // that haven't issued a secret continue to accept the bare header.
    const signed = options.getSignedIdentity?.()
    if (signed && signed.userHash && signed.exp) {
      headers['X-Mhosaic-User-Hmac'] = signed.userHash
      headers['X-Mhosaic-User-Exp'] = String(signed.exp)
      if (signed.email) headers['X-Mhosaic-User-Email'] = signed.email
    }
    return headers
  }

  async function listMine(externalId: string): Promise<WidgetReportRow[]> {
    const response = await fetcher(`${endpoint}/api/feedback/v1/reports/widget/mine/`, {
      method: 'GET',
      headers: widgetHeaders(externalId),
    })
    if (response.status === 404) return [] // submitter has no thread yet
    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(`listMine failed: ${response.status} ${text}`)
    }
    return response.json() as Promise<WidgetReportRow[]>
  }

  async function listChangelog(externalId: string): Promise<WidgetChangelogRow[]> {
    const response = await fetcher(
      `${endpoint}/api/feedback/v1/reports/widget/changelog/`,
      { method: 'GET', headers: widgetHeaders(externalId) },
    )
    if (response.status === 404) return []
    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(`listChangelog failed: ${response.status} ${text}`)
    }
    return response.json() as Promise<WidgetChangelogRow[]>
  }

  async function getReport(
    reportId: string,
    externalId: string,
  ): Promise<WidgetReportDetail> {
    const response = await fetcher(
      `${endpoint}/api/feedback/v1/reports/widget/${reportId}/`,
      { method: 'GET', headers: widgetHeaders(externalId) },
    )
    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(`getReport failed: ${response.status} ${text}`)
    }
    return response.json() as Promise<WidgetReportDetail>
  }

  async function addComment(
    reportId: string,
    externalId: string,
    body: string,
    clientNonce?: string,
  ): Promise<WidgetCommentRow> {
    const response = await fetcher(
      `${endpoint}/api/feedback/v1/reports/widget/${reportId}/comments/`,
      {
        method: 'POST',
        headers: {
          ...widgetHeaders(externalId),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          body,
          ...(clientNonce !== undefined && { client_nonce: clientNonce }),
        }),
      },
    )
    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(`addComment failed: ${response.status} ${text}`)
    }
    return response.json() as Promise<WidgetCommentRow>
  }

  async function closeAsResolved(
    reportId: string,
    externalId: string,
  ): Promise<WidgetReportDetail> {
    const response = await fetcher(
      `${endpoint}/api/feedback/v1/reports/widget/${reportId}/`,
      {
        method: 'PATCH',
        headers: {
          ...widgetHeaders(externalId),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'closed' }),
      },
    )
    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(`closeAsResolved failed: ${response.status} ${text}`)
    }
    return response.json() as Promise<WidgetReportDetail>
  }

  async function reopenUnresolved(
    reportId: string,
    externalId: string,
  ): Promise<WidgetReportDetail> {
    const response = await fetcher(
      `${endpoint}/api/feedback/v1/reports/widget/${reportId}/`,
      {
        method: 'PATCH',
        headers: {
          ...widgetHeaders(externalId),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'in_progress' }),
      },
    )
    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(`reopenUnresolved failed: ${response.status} ${text}`)
    }
    return response.json() as Promise<WidgetReportDetail>
  }

  function boardQueryString(filters?: BoardFilters): string {
    if (!filters) return ''
    const params = new URLSearchParams()
    // Multi-value fields use append() so repeated keys stack the way DRF's
    // `request.query_params.getlist()` reads them on the server.
    filters.status?.forEach((s) => params.append('status', s))
    filters.type?.forEach((t) => params.append('type', t))
    filters.severity?.forEach((s) => params.append('severity', s))
    if (filters.q) params.set('q', filters.q)
    if (filters.mine) params.set('mine', '1')
    if (filters.ordering) params.set('ordering', filters.ordering)
    if (filters.page && filters.page > 1) params.set('page', String(filters.page))
    const qs = params.toString()
    return qs ? `?${qs}` : ''
  }

  async function listBoard(
    externalId: string,
    filters?: BoardFilters,
  ): Promise<BoardListPage> {
    const response = await fetcher(
      `${endpoint}/api/feedback/v1/reports/widget/board/${boardQueryString(filters)}`,
      { method: 'GET', headers: widgetHeaders(externalId) },
    )
    if (response.status === 404) {
      // Caller has no WidgetUser row yet — render an empty Board rather
      // than a hard error so the first-time experience is a clean slate.
      return { count: 0, next: null, previous: null, results: [] }
    }
    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(`listBoard failed: ${response.status} ${text}`)
    }
    return response.json() as Promise<BoardListPage>
  }

  async function fetchBoardKpis(
    externalId: string,
    filters?: BoardFilters,
  ): Promise<WidgetBoardKpis> {
    const response = await fetcher(
      `${endpoint}/api/feedback/v1/reports/widget/board/kpis/${boardQueryString(filters)}`,
      { method: 'GET', headers: widgetHeaders(externalId) },
    )
    if (response.status === 404) {
      return { total: 0, by_status: {}, resolution_rate: 0, scope: 'mine' }
    }
    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(`fetchBoardKpis failed: ${response.status} ${text}`)
    }
    return response.json() as Promise<WidgetBoardKpis>
  }

  async function checkVisibility(
    externalId: string,
    email?: string,
  ): Promise<boolean> {
    const response = await fetcher(
      `${endpoint}/api/feedback/v1/widget/visibility/`,
      {
        method: 'POST',
        headers: {
          ...widgetHeaders(externalId),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          external_id: externalId,
          ...(email ? { email } : {}),
        }),
      },
    )
    if (!response.ok) {
      throw new Error(`checkVisibility failed: ${response.status}`)
    }
    const data = (await response.json()) as { show?: boolean }
    return Boolean(data.show)
  }

  return {
    submitReport,
    checkVisibility,
    listMine,
    listChangelog,
    getReport,
    addComment,
    closeAsResolved,
    reopenUnresolved,
    listBoard,
    fetchBoardKpis,
  }
}
